/**
 * Layout and display building blocks shared by every page.
 * Spacing scale: page sections gap-8, panel padding p-6 with gap-5, fields gap-5.
 */
import type { Channel, MessageStatus } from "@aiyo/shared";
import { Check, CircleCheck, CircleInfo, Copy, Tray, TriangleExclamation } from "@gravity-ui/icons";
import { Alert, Button, Card, Chip, EmptyState, Spinner, Tooltip } from "@heroui/react";
import { type ComponentType, type ReactNode, type SVGProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { useErrorMessage } from "../lib/format";

type ChipColor = "default" | "accent" | "success" | "warning" | "danger";
type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5">
        {breadcrumb ? <nav className="text-sm text-muted">{breadcrumb}</nav> : null}
        <h1 className="text-2xl font-semibold tracking-tight text-balance">{title}</h1>
        {description ? <p className="max-w-2xl text-sm leading-relaxed text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** Page body: header then sections, with one vertical rhythm. */
export function Page({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-8">{children}</div>;
}

/** A titled card that groups related content or fields. */
export function Panel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={`gap-5 p-6 ${className ?? ""}`}>
      {title || actions ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            {title ? <h2 className="text-base font-semibold">{title}</h2> : null}
            {description ? (
              <p className="max-w-2xl text-sm leading-relaxed text-muted">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </Card>
  );
}

const MESSAGE_COLORS: Record<MessageStatus, ChipColor> = {
  scheduled: "default",
  queued: "default",
  processing: "accent",
  completed: "success",
  partially_failed: "warning",
  failed: "danger",
  cancelled: "default",
};

export function MessageStatusChip({ status }: { status: MessageStatus }) {
  const { t } = useTranslation();
  return (
    <Chip size="sm" variant="soft" color={MESSAGE_COLORS[status]}>
      {t(`status.message.${status}`)}
    </Chip>
  );
}

/** Generic state pill: true = good, "warn" = needs attention, false = off. */
export function StateChip({ ok, label }: { ok: boolean | "warn"; label: string }) {
  return (
    <Chip size="sm" variant="soft" color={ok === "warn" ? "warning" : ok ? "success" : "default"}>
      {label}
    </Chip>
  );
}

export function ChannelChip({ channel }: { channel: Channel }) {
  const { t } = useTranslation();
  return (
    <Chip size="sm" variant="secondary">
      {t(`channels.${channel}`)}
    </Chip>
  );
}

export function LoadingBlock() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-3 py-20 text-sm text-muted" role="status">
      <Spinner size="sm" />
      {t("common.loading")}
    </div>
  );
}

export function ErrorBlock({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  return (
    <Alert status="danger">
      <Alert.Indicator>
        <TriangleExclamation />
      </Alert.Indicator>
      <Alert.Content>
        <Alert.Title>{t("errors.generic")}</Alert.Title>
        <Alert.Description>{message(error)}</Alert.Description>
      </Alert.Content>
      {onRetry ? (
        <Button size="sm" variant="secondary" onPress={onRetry}>
          {t("common.retry")}
        </Button>
      ) : null}
    </Alert>
  );
}

export function InlineError({ error }: { error: unknown }) {
  const message = useErrorMessage();
  if (!error) return null;
  // A tinted box rather than <Alert>: an Alert has the same tone as cards and dialogs, so it would not stand out there.
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-xl bg-danger-soft px-3.5 py-3 text-sm text-danger-soft-foreground"
    >
      <TriangleExclamation className="mt-0.5 size-4 shrink-0" />
      <span>{message(error)}</span>
    </div>
  );
}

export function EmptyBlock({
  title,
  hint,
  action,
  icon: IconCmp = Tray,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  icon?: Icon;
}) {
  return (
    <EmptyState className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <span className="flex size-11 items-center justify-center rounded-full bg-default text-muted">
        <IconCmp className="size-5" />
      </span>
      <div className="flex flex-col gap-1">
        <div className="font-medium">{title}</div>
        {hint ? <p className="max-w-sm text-sm text-muted">{hint}</p> : null}
      </div>
      {action ? <div className="mt-2">{action}</div> : null}
    </EmptyState>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip delay={300}>
      <Button
        size="sm"
        variant="ghost"
        isIconOnly={!label}
        aria-label={t("common.copy")}
        onPress={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? <Check /> : <Copy />}
        {label ? (copied ? t("common.copied") : label) : null}
      </Button>
      <Tooltip.Content>
        <p>{copied ? t("common.copied") : t("common.copy")}</p>
      </Tooltip.Content>
    </Tooltip>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="inline-block max-w-full truncate rounded-md bg-default px-1.5 py-0.5 align-middle font-mono text-[12.5px] whitespace-nowrap">
      {children}
    </code>
  );
}

export function CodeBlock({ value, copy = true }: { value: string; copy?: boolean }) {
  return (
    <div className="relative min-w-0">
      <pre className="max-h-[420px] overflow-auto rounded-xl bg-surface-secondary p-4 pr-12 font-mono text-[12.5px] leading-relaxed">
        {value}
      </pre>
      {copy ? (
        <div className="absolute top-2 right-2">
          <CopyButton value={value} />
        </div>
      ) : null}
    </div>
  );
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-[max-content_minmax(0,1fr)]">
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-sm text-muted">{item.label}</dt>
          <dd className="min-w-0 text-sm break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: ChipColor }) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "danger"
        ? "text-danger"
        : tone === "warning"
          ? "text-warning"
          : tone === "accent"
            ? "text-accent"
            : "";
  return (
    <Card className="gap-2 p-5">
      <div className="text-sm text-muted">{label}</div>
      <div className={`text-2xl font-semibold tracking-tight ${toneClass}`}>{value}</div>
    </Card>
  );
}

/** Destructive actions, always last on a page and outlined in the danger colour. */
export function DangerZone({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Panel title={title} description={description} className="border border-danger/30">
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

const CALLOUT = {
  warning: { box: "bg-warning-soft text-warning-soft-foreground", icon: TriangleExclamation },
  success: { box: "bg-success-soft text-success-soft-foreground", icon: CircleCheck },
  info: { box: "bg-accent-soft text-accent-soft-foreground", icon: CircleInfo },
} as const;

/** Tinted notice for cards and dialogs (like InlineError, which is the danger variant). */
export function Callout({ tone, children }: { tone: keyof typeof CALLOUT; children: ReactNode }) {
  const { box, icon: IconCmp } = CALLOUT[tone];
  return (
    <div
      role={tone === "warning" ? "alert" : "status"}
      className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-sm ${box}`}
    >
      <IconCmp className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
